import { usePermission } from "../hooks/usePermission";
import React, { useEffect, useState, useMemo } from "react";
import api from "../api/api";
import toast from "react-hot-toast";
import { Plus, X, Check, XCircle, Search, Filter, User, CalendarDays, Pencil, Trash2, MessageSquare, Send } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const statusColors: any = {
  PENDING: "bg-yellow-100 text-yellow-700 border-yellow-200",
  APPROVED: "bg-green-100 text-green-700 border-green-200",
  REJECTED: "bg-red-100 text-red-700 border-red-200",
};
const statusDot: any = {
  PENDING: "bg-yellow-400",
  APPROVED: "bg-green-500",
  REJECTED: "bg-red-500",
};

const Approvals: React.FC = () => {
  const { isAdmin, user } = useAuth() as any;
  const canEditPerm   = usePermission("approvals", "edit");
  const canDeletePerm = usePermission("approvals", "delete");
  const [approvals, setApprovals] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [form, setForm] = useState({ type: "", initialComment: "", assignedToId: "" });
  const [editForm, setEditForm] = useState({ type: "", assignedToId: "" });
  const [viewApproval, setViewApproval] = useState<any|null>(null);
  const [aprComments, setAprComments] = useState<any[]>([]);
  const [newAprComment, setNewAprComment] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterReviewer, setFilterReviewer] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/approvals");
      setApprovals(r.data);
    } catch (err) {
      console.error("Failed to load approvals", err);
    }
  };

  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.type.trim()) { toast.error("Please fill the request type"); return; }
    try {
      const payload: any = { type: form.type };
      if (form.assignedToId) payload.assignedToId = form.assignedToId;
      const res = await api.post("/approvals", payload);
      if (form.initialComment?.trim() && res.data?.id) {
        await api.post(`/approvals/${res.data.id}/comments`, { content: form.initialComment }).catch(() => {});
      }
      setShowForm(false);
      setForm({ type: "", initialComment: "", assignedToId: "" });
      await load();
      toast.success("Request submitted successfully!");
    } catch { toast.error("Failed to submit request"); }
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm.type.trim()) { toast.error("Please fill the request type"); return; }
    try {
      await api.put(`/approvals/${editTarget.id}`, {
        type: editForm.type,
        assignedToId: editForm.assignedToId || null,
      });
      setEditTarget(null);
      await load();
      toast.success("Approval updated!");
    } catch { toast.error("Failed to update"); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/approvals/${deleteTarget.id}`);
      setDeleteTarget(null);
      await load();
      toast.success("Approval deleted!");
    } catch { toast.error("Failed to delete"); }
  };

  const review = async (id: string, status: string) => {
    try {
      await api.put(`/approvals/${id}/review`, { status });
      await load();
      toast.success(status === "APPROVED" ? "Approved successfully!" : "Rejected successfully!");
    } catch { toast.error("Failed"); }
  };

  const openViewApproval = async (a: any) => {
    setViewApproval(a);
    try {
      const r = await api.get(`/approvals/${a.id}/comments`);
      setAprComments(r.data);
    } catch { setAprComments(a.comments || []); }
  };

  const submitAprComment = async () => {
    if (!newAprComment.trim() || !viewApproval) return;
    try {
      const r = await api.post(`/approvals/${viewApproval.id}/comments`, { content: newAprComment });
      setAprComments(prev => [...prev, r.data]);
      setNewAprComment("");
    } catch { toast.error("Failed to add comment"); }
  };

  const openEdit = (a: any) => {
    setEditForm({ type: a.type, assignedToId: a.assignedToId || "" });
    setEditTarget(a);
  };

  const resetFilters = () => {
    setSearch(""); setFilterStatus(""); setFilterFrom(""); setFilterTo("");
    setFilterEmployee(""); setFilterReviewer("");
  };

  const hasFilters = search || filterStatus || filterFrom || filterTo || filterEmployee || filterReviewer;

  const canEditOrDelete = (a: any) => canEditPerm || canDeletePerm || a.requestedById === user?.id || a.assignedToId === user?.id;
  const canReview = (a: any) => (canEditPerm || a.assignedToId === user?.id) && a.status === "PENDING";

  const filtered = useMemo(() => {
    let result = approvals;
    if (filterStatus) result = result.filter(a => a.status === filterStatus);
    if (filterEmployee) result = result.filter(a => a.requestedById === filterEmployee);
    if (filterReviewer) result = result.filter(a => a.reviewedById === filterReviewer || a.assignedToId === filterReviewer);
    if (filterFrom) result = result.filter(a => new Date(a.createdAt) >= new Date(filterFrom));
    if (filterTo) result = result.filter(a => new Date(a.createdAt) <= new Date(filterTo + "T23:59:59"));
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        (a.requestedBy?.name || "").toLowerCase().includes(q) ||
        (a.assignedTo?.name || "").toLowerCase().includes(q) ||
        (a.reviewedBy?.name || "").toLowerCase().includes(q) ||
        (a.type || "").toLowerCase().includes(q) ||
        (a.type || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [approvals, search, filterStatus, filterFrom, filterTo, filterEmployee, filterReviewer]);

  const counts = useMemo(() => ({
    all: approvals.length,
    pending: approvals.filter(a => a.status === "PENDING").length,
    approved: approvals.filter(a => a.status === "APPROVED").length,
    rejected: approvals.filter(a => a.status === "REJECTED").length,
  }), [approvals]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Approvals</h2>
          <p className="text-sm text-gray-500 mt-0.5">{filtered.length} of {approvals.length} requests</p>
        </div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
          <Plus size={16} /> New Request
        </button>
      </div>

      {/* Status summary tabs */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "All", key: "", count: counts.all, color: "bg-gray-50 border-gray-200 text-gray-700" },
          { label: "Pending", key: "PENDING", count: counts.pending, color: "bg-yellow-50 border-yellow-200 text-yellow-700" },
          { label: "Approved", key: "APPROVED", count: counts.approved, color: "bg-green-50 border-green-200 text-green-700" },
          { label: "Rejected", key: "REJECTED", count: counts.rejected, color: "bg-red-50 border-red-200 text-red-700" },
        ].map(tab => (
          <button key={tab.key} onClick={() => setFilterStatus(tab.key)}
            className={`rounded-xl border p-3 text-left transition-all ${tab.color} ${filterStatus === tab.key ? "ring-2 ring-blue-400" : "hover:opacity-80"}`}>
            <div className="text-2xl font-bold">{tab.count}</div>
            <div className="text-xs font-medium mt-0.5">{tab.label}</div>
          </button>
        ))}
      </div>

      {/* Filter Panel */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Filter size={14} /> <span>Search & Filter</span>
          {hasFilters && (
            <button onClick={resetFilters} className="ml-auto flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-normal">
              <X size={12} /> Reset
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by employee, reviewer, type..."
                className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"><X size={14} /></button>}
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Requested By</label>
            <select value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
              <option value="">All Employees</option>
              {users.map((u: any) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Assigned Reviewer</label>
            <select value={filterReviewer} onChange={e => setFilterReviewer(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
              <option value="">All Reviewers</option>
              {users.map((u: any) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Approvals List */}
      <div className="space-y-3">
        {filtered.map(a => (
          <div key={a.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="font-semibold text-gray-800 text-base">{a.type}</span>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium border flex items-center gap-1 ${statusColors[a.status]}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusDot[a.status]}`} />
                    {a.status}
                  </span>
                </div>
                <button onClick={() => openViewApproval(a)}
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1 mb-3">
                  <MessageSquare size={12}/> View Comments ({a.comments?.length || 0})
                </button>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[9px] font-bold">
                      {(a.requestedBy?.name || "?")[0].toUpperCase()}
                    </div>
                    <span className="text-gray-600 font-medium">{a.requestedBy?.name}</span>
                  </span>
                  {a.assignedTo && (
                    <span className="flex items-center gap-1">
                      → Reviewer:
                      <div className="w-4 h-4 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-[9px] font-bold">
                        {a.assignedTo.name[0].toUpperCase()}
                      </div>
                      <span className="text-purple-600 font-medium">{a.assignedTo.name}</span>
                    </span>
                  )}
                  {a.reviewedBy && a.status !== "PENDING" && (
                    <span>• Reviewed by: <span className="text-gray-600 font-medium">{a.reviewedBy.name}</span></span>
                  )}
                  <span>• {new Date(a.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-2 flex-shrink-0">
                {/* Approve / Reject */}
                {canReview(a) && (
                  <div className="flex gap-2">
                    <button onClick={() => review(a.id, "APPROVED")}
                      className="flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 px-3 py-1.5 rounded-lg text-sm hover:bg-green-100 font-medium transition-colors">
                      <Check size={14} /> Approve
                    </button>
                    <button onClick={() => review(a.id, "REJECTED")}
                      className="flex items-center gap-1.5 bg-red-50 border border-red-200 text-red-700 px-3 py-1.5 rounded-lg text-sm hover:bg-red-100 font-medium transition-colors">
                      <XCircle size={14} /> Reject
                    </button>
                  </div>
                )}
                {/* Edit / Delete */}
                {canEditOrDelete(a) && (
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => openEdit(a)}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 border border-gray-200 px-2.5 py-1.5 rounded-lg transition-colors">
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => setDeleteTarget(a)}
                      className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 border border-gray-200 px-2.5 py-1.5 rounded-lg transition-colors">
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200">
            <Search size={36} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">No approvals found</p>
            {hasFilters && <button onClick={resetFilters} className="mt-2 text-sm text-blue-500 hover:underline">Clear filters</button>}
          </div>
        )}
      </div>

      {/* New Request Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">New Approval Request</h3>
              <button onClick={() => { setShowForm(false); setForm({ type: "", initialComment: "", assignedToId: "" }); }} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Request Type *</label>
                <input value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  placeholder="e.g. Leave Request, Budget Approval..."
                  required className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Comment</label>
                <textarea value={form.initialComment} onChange={e => setForm(f => ({ ...f, initialComment: e.target.value }))}
                  placeholder="Add an initial comment (optional)..."
                  rows={3} className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1">
                  <User size={11} /> Assign Reviewer <span className="text-gray-400">(optional)</span>
                </label>
                <select value={form.assignedToId} onChange={e => setForm(f => ({ ...f, assignedToId: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
                  <option value="">Select reviewer...</option>
                  {users.filter((u: any) => u.id !== user?.id).map((u: any) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role === "ADMIN" ? "Admin" : "Employee"})</option>
                  ))}
                </select>
                {form.assignedToId && (
                  <p className="text-xs text-blue-500 mt-1.5">Email notification will be sent to the selected reviewer.</p>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setForm({ type: "", initialComment: "", assignedToId: "" }); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-xl text-sm hover:bg-gray-50 font-medium">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm hover:bg-blue-700 font-medium">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Edit Approval Request</h3>
              <button onClick={() => setEditTarget(null)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1"><X size={20} /></button>
            </div>
            <form onSubmit={submitEdit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Request Type *</label>
                <input value={editForm.type} onChange={e => setEditForm(f => ({ ...f, type: e.target.value }))}
                  placeholder="e.g. Leave Request, Budget Approval..."
                  required className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1">
                  <User size={11} /> Assign Reviewer <span className="text-gray-400">(optional)</span>
                </label>
                <select value={editForm.assignedToId} onChange={e => setEditForm(f => ({ ...f, assignedToId: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
                  <option value="">Select reviewer...</option>
                  {users.filter((u: any) => u.id !== user?.id).map((u: any) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role === "ADMIN" ? "Admin" : "Employee"})</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditTarget(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-xl text-sm hover:bg-gray-50 font-medium">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm hover:bg-blue-700 font-medium">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <Trash2 size={22} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Approval?</h3>
              <p className="text-sm text-gray-500 mb-1">Are you sure you want to delete:</p>
              <p className="text-sm font-semibold text-gray-800 mb-6">"{deleteTarget.type}"</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteTarget(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-xl text-sm hover:bg-gray-50 font-medium">Cancel</button>
                <button onClick={confirmDelete}
                  className="flex-1 bg-red-600 text-white py-2.5 rounded-xl text-sm hover:bg-red-700 font-medium">Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Comments Modal */}
      {viewApproval && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-4 shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{viewApproval.type}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium border flex items-center gap-1 ${statusColors[viewApproval.status]}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusDot[viewApproval.status]}`} />
                    {viewApproval.status}
                  </span>
                  <span className="text-xs text-gray-400">{new Date(viewApproval.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
              <button onClick={() => { setViewApproval(null); setAprComments([]); setNewAprComment(""); }} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {viewApproval.requestedBy && (
                  <div><span className="text-xs text-gray-400">Requested By</span><p className="text-gray-700">{viewApproval.requestedBy.name}</p></div>
                )}
                {viewApproval.assignedTo && (
                  <div><span className="text-xs text-gray-400">Reviewer</span><p className="text-gray-700">{viewApproval.assignedTo.name}</p></div>
                )}
                {viewApproval.reviewedBy && viewApproval.status !== "PENDING" && (
                  <div><span className="text-xs text-gray-400">Reviewed By</span><p className="text-gray-700">{viewApproval.reviewedBy.name}</p></div>
                )}
              </div>

              {/* Comments */}
              <div className="border-t border-gray-100 pt-3">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-3">
                  <MessageSquare size={14}/> Comments ({aprComments.length})
                </h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {aprComments.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No comments yet</p>}
                  {aprComments.map((c: any) => (
                    <div key={c.id} className="bg-gray-50 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-gray-700">{c.author?.name}</span>
                        <span className="text-xs text-gray-400">{new Date(c.createdAt).toLocaleString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</span>
                      </div>
                      <p className="text-sm text-gray-600">{c.content}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <input value={newAprComment} onChange={e => setNewAprComment(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitAprComment(); } }}
                    placeholder="Add a comment..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
                  <button onClick={submitAprComment} disabled={!newAprComment.trim()}
                    className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors">
                    <Send size={16}/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Approvals;
